'use client';

import { css } from '@bts-web/utils-style-engine';
import React, { FC, ReactNode, useRef, useState, useMemo } from 'react';
import { useClickAwayListener } from '../../common/components/ClickAwayListener/useClickAwayListener';
import { useUserAccessControl } from '../../common/utils/userAccess';
import { AssetDetailsContainerInTradeWrapper } from './AssetDetailsContainerInTradeWrapper';
import { TradeBoxContainerInTradeWrapper } from './TradeBoxContainerInTradeWrapper';

export type AssetDetailTradeWrapperProps = {
  AssetDetailsContent: ReactNode;
  TradeStateContent: ReactNode;
  hideMobile: 'tradeElement' | 'assetElement';
  startFocusValue?: boolean;
};

export const AssetDetailTradeWrapper: FC<AssetDetailTradeWrapperProps> = ({
  AssetDetailsContent,
  TradeStateContent,
  hideMobile,
  startFocusValue,
}) => {
  const ref = useRef<HTMLDivElement>(null as unknown as HTMLDivElement);

  const { isPepFailed } = useUserAccessControl();

  const [isTradeFocused, setIsTradeFocused] = useState(
    startFocusValue ?? false,
  );

  const handleClickAway = () => {
    setIsTradeFocused(false);
  };

  useClickAwayListener(ref, handleClickAway);

  const handleTradeClick = () => {
    setIsTradeFocused(true);
  };

  const assetDetailsMemo = useMemo(
    () => (
      <AssetDetailsContainerInTradeWrapper
        hideOnMobile={hideMobile === 'assetElement'}
      >
        {AssetDetailsContent}
      </AssetDetailsContainerInTradeWrapper>
    ),
    [AssetDetailsContent, hideMobile],
  );

  const tradeBoxMemo = useMemo(
    () => (
      <TradeBoxContainerInTradeWrapper
        refProp={ref}
        hideMobile={hideMobile}
        hideOnMobile={hideMobile === 'tradeElement'}
        onClick={handleTradeClick}
      >
        {TradeStateContent}
      </TradeBoxContainerInTradeWrapper>
    ),
    [TradeStateContent, hideMobile],
  );

  return (
    <>
      {isTradeFocused ? (
        <div
          data-testid="trade-overlay"
          className={css({
            display: { base: 'none', lg: 'block' },
            background: 'rgba(166, 166, 166, 0.50)',
            position: 'fixed',
            backdropFilter: 'blur(6px)',
            top: 0,
            insetInline: 0,
            insetInlineEnd: 0,
            bottom: 0,
            zIndex: 5,
          })}
        />
      ) : null}

      <div
        className={css({
          display: 'flex',
          width: '100%',
          flexGrow: '1',
          alignItems: 'stretch',
          background: 'screen_background.primary',
        })}
      >
        {assetDetailsMemo}

        {!isPepFailed && tradeBoxMemo}
      </div>
    </>
  );
};
